import React, { useMemo } from "react"
import { IconInfo, IconWarn } from "dash-styled"
import { ReactComponent as IconSpinner } from "icons/lib/spinner.svg"
import { useDispatch } from "react-redux"
import { getEmail } from "../../../../../Helpers/Functions/Selectors/auth"
import { AppStatus } from "../../../../../Models/Client/AppStatus"
import { routePaths, abstractRoutePaths } from "../../../../../Routes"
import { matchPath } from "react-router"
import { push } from "connected-react-router"
import { useAppState } from "../../../../../Hooks/useReduxState"
import { openModal } from "../../../../../Actions/Client/ModalActions"
import { ModalViewName } from "../../../../../Models/Client/Modal"
import { getValue, hasValue } from "../../../../../Models/Client/FetchState"
import { useBoolean } from "../../../../../Hooks/useBoolean"
import { Confirm } from "../../../../Common/Modal/Confirm"
import { useAppTemplate } from "../../../../../Hooks/useAppTemplate"
import { acceptCalibrationJobs } from "../../../../../Actions/Client/Browser"
import { AppStatusBannerTemplate } from "./AppStatusBannerTemplate"
import { useAppStatus } from "../../../../../Hooks/useAppStatus"
import { useAssumedRoles } from "../../../../../Hooks/useAssumedRoles"
import { useAppInstance } from "../../../../../Hooks/useAppInstance"
import {
    hasAppPermission,
    ProjectRole
} from "../../../../../Models/Client/ProjectRole"
import { AppStatusCanoticCrowd } from "../../../../../Models/Client/AppStatusCanoticCrowd"
import { AppStatusPrivateLabeling } from "../../../../../Models/Client/AppStatusPrivateLabeling"
import { Link } from "react-router-dom"
import { unArchiveApps } from "../../../../../Actions/API/AppInstance/AppInstanceActions"
import { isAppInstanceArchived } from "../../../../../Models/API/AppInstanceModel"
import { useAppDetailsPaths } from "../../../../../Hooks/useAppDetailsPaths"
import { AppWorkerRole } from "../../../../../Models/API/AppWorkerRole"
import { AppStatusGeneral } from "../../../../../Models/Client/AppStatusGeneral"

interface AppStatusBannerProps {
    orgUsername: string
    appID: string
    canLabel: boolean
}

interface AppStatusBannerIsolatedProps {
    orgUsername: string
    appID: string
    type: AppStatus
    email: string
    enterprise: boolean
    roles: ProjectRole[]
    onClickAddData?: () => void
    onClickReview?: () => void
    onClickChoosePackage?: () => void
    onClickAddPaymentMethod?: () => void
    onClickAcceptCalibrationJobs?: () => void
    onClickLabelMyself?: () => void
    onClickReviewTaskDesign?: () => void
}

function AppStatusBannerIsolated({
    orgUsername,
    appID,
    type,
    email,
    enterprise,
    roles,
    onClickAddData,
    onClickReview,
    onClickChoosePackage,
    onClickAddPaymentMethod,
    onClickAcceptCalibrationJobs,
    onClickLabelMyself,
    onClickReviewTaskDesign
}: AppStatusBannerIsolatedProps) {
    switch (type) {
        case AppStatusCanoticCrowd.calculatingMetrics:
            return (
                <AppStatusBannerTemplate
                    bgColor="#86798A"
                    title="We're calculating your performance options"
                    message="You can then choose the quality and price for your data processing"
                    icon={<IconSpinner className="w-6 h-6" />}
                />
            )
        case AppStatusCanoticCrowd.jobSubmitRequired: {
            return (
                <AppStatusBannerTemplate
                    bgColor="#EC652F"
                    title={`Upload more data points`}
                    message="to unlock your performance options"
                    icon={<IconInfo size="xl" />}
                    action={["Upload data", onClickAddData]}
                />
            )
        }
        case AppStatusCanoticCrowd.labeling:
            return (
                <AppStatusBannerTemplate
                    bgColor="#18B5A2"
                    icon={<IconSpinner className="w-6 h-6" />}
                    title="We're processing your data"
                    message={`When we're done we'll message you at ${email}`}
                    action={["Add more data", onClickAddData]}
                />
            )
        case AppStatusCanoticCrowd.reviewRequired: {
            return (
                <AppStatusBannerTemplate
                    bgColor="#CD44F3"
                    icon={<IconInfo size="xl" />}
                    title="You have results ready to review"
                    action={["Review results", onClickReview]}
                />
            )
        }
        case AppStatusCanoticCrowd.configureMetricsRequired: {
            return (
                <AppStatusBannerTemplate
                    bgColor="#EA3F80"
                    icon={<IconWarn size="xl" />}
                    title="Choose your performance settings to continue"
                    action={["Choose package", onClickChoosePackage]}
                />
            )
        }
        case AppStatusCanoticCrowd.labelingSuspended: {
            return (
                <AppStatusBannerTemplate
                    bgColor="#EA3F80"
                    icon={<IconWarn size="xl" />}
                    title={"Processing is paused"}
                    message={
                        enterprise
                            ? "Your customer success agent has been notified. Please contact them for more information"
                            : "Your performance options have changed — please review them to resume processing"
                    }
                    action={["Choose package", onClickChoosePackage]}
                />
            )
        }
        case AppStatusCanoticCrowd.paymentMethodMissing: {
            return (
                <AppStatusBannerTemplate
                    bgColor="#F5A623"
                    icon={<IconWarn size="xl" />}
                    title="We need your billing details to continue"
                    action={["Add billing info", onClickAddPaymentMethod]}
                />
            )
        }
        case AppStatusCanoticCrowd.acceptCalibrationJobsRequired: {
            return (
                <AppStatusBannerTemplate
                    bgColor="#F5A623"
                    icon={<IconWarn size="xl" />}
                    title="We need your billing details to continue"
                    action={["Add billing info", onClickAcceptCalibrationJobs]}
                />
            )
        }
        case AppStatusPrivateLabeling.uploadSuggested:
            return (
                <AppStatusBannerTemplate
                    bgColor="#EC652F"
                    icon={<IconInfo size="xl" />}
                    title="Add some data to get started"
                    action={["Upload data", onClickAddData]}
                />
            )
        case AppStatusPrivateLabeling.inviteSuggested:
            if (onClickLabelMyself === undefined) {
                return null
            }

            return (
                <AppStatusBannerTemplate
                    bgColor="#86798A"
                    icon={<IconInfo size="xl" />}
                    title="You're ready to process"
                    message={
                        <>
                            You can also{" "}
                            <Link
                                to={routePaths.appCollaborators(
                                    orgUsername,
                                    appID,
                                    undefined,
                                    "invite"
                                )}
                                className="text-white"
                            >
                                invite other people
                            </Link>{" "}
                            to help you out.
                        </>
                    }
                    suppressPanelClick={true}
                    action={[
                        roles.length === 1 && roles[0] === AppWorkerRole.LABELER
                            ? "Start processing"
                            : "Process myself",
                        onClickLabelMyself
                    ]}
                />
            )
        case AppStatusGeneral.paramsSchemaMismatch:
            return (
                <AppStatusBannerTemplate
                    bgColor="#EC652F"
                    icon={<IconWarn size="xl" />}
                    title="Job design is out-of-date"
                    message="The data program has upgraded — please update job design to continue processing."
                    action={["Review job design", onClickReviewTaskDesign]}
                />
            )
    }
}

export function AppStatusBanner({
    orgUsername,
    appID,
    canLabel
}: AppStatusBannerProps) {
    const instanceFetch = useAppInstance(appID)
    const onboarding = useAppState(
        state => state.api.interface.templateInstantiate
    )
    const rolesFetch = useAssumedRoles(appID)
    const appStatusFetch = useAppStatus(appID)
    const templateFetch = useAppTemplate(appID)
    const currentPath = useAppState(state => state.router.location.pathname)
    const email = useAppState(getEmail)
    const [isMessagesPath] = useAppDetailsPaths()
    const dispatch = useDispatch()
    const handleAddData =
        hasAppPermission(getValue(rolesFetch) ?? [], "job:submit") &&
        !matchPath(currentPath, abstractRoutePaths.appAddData)
            ? () =>
                  dispatch(
                      push(routePaths.appAddData(orgUsername, appID, "input"))
                  )
            : undefined
    const [isConfirmShown, showConfirm, hideConfirm] = useBoolean(false)
    const handleReview =
        onboarding === null &&
        matchPath(currentPath, routePaths.appDetail(orgUsername, appID))
            ? undefined
            : () => {
                  const path = routePaths.appDetailReviewJobs(
                      orgUsername,
                      appID
                  )

                  dispatch(push(path))
              }
    const handleChoosePackage =
        hasAppPermission(getValue(rolesFetch) ?? [], "settings:update") &&
        !matchPath(currentPath, abstractRoutePaths.appCustomizeMetrics)
            ? () =>
                  dispatch(
                      push(routePaths.appCustomizeMetrics(orgUsername, appID))
                  )
            : undefined
    const handleClickAcceptCalibrationJobs = () => {
        dispatch(acceptCalibrationJobs(appID))
        hideConfirm()
        dispatch(openModal(ModalViewName.billing))
    }
    const handleAddPaymentMethod = hasAppPermission(
        getValue(rolesFetch) ?? [],
        "settings:update"
    )
        ? () => dispatch(openModal(ModalViewName.billing))
        : undefined

    const handleLabelMyself = useMemo(() => {
        if (
            !canLabel ||
            !hasAppPermission(getValue(rolesFetch) ?? [], "task:label")
        ) {
            return undefined
        }

        const labelingPath = routePaths.appDetailLabeling(orgUsername, appID)

        return () => dispatch(push(labelingPath, { isMessagesPath }))
    }, [canLabel, rolesFetch, dispatch, orgUsername, appID, isMessagesPath])
    const handleReviewTaskDesign = useMemo(() => {
        if (
            matchPath(
                currentPath,
                abstractRoutePaths.appCustomizeJobParameters
            ) ||
            !hasAppPermission(getValue(rolesFetch) ?? [], "task-design:view")
        ) {
            return undefined
        }

        const path = routePaths.appCustomizeJobParameters(orgUsername, appID)

        return () => dispatch(push(path))
    }, [currentPath, rolesFetch, orgUsername, appID, dispatch])
    const handleUnarchivingApp = () =>
        dispatch(unArchiveApps(orgUsername, [appID]))

    if (hasValue(instanceFetch) && isAppInstanceArchived(instanceFetch.value)) {
        return (
            <AppStatusBannerTemplate
                bgColor="#86798A"
                title="This project is archived"
                message="To perform any actions on it, unarchive it."
                icon={<IconWarn size="xl" />}
                action={["Unarchive", handleUnarchivingApp]}
            />
        )
    }
    if (
        !hasValue(rolesFetch) ||
        !hasValue(templateFetch) ||
        !hasValue(instanceFetch) ||
        !hasValue(appStatusFetch) ||
        appStatusFetch.value === undefined
    ) {
        return null
    }

    const price = templateFetch.value.measurement_job_cost

    return (
        <>
            <AppStatusBannerIsolated
                orgUsername={orgUsername}
                appID={appID}
                type={appStatusFetch.value}
                email={email || ""}
                enterprise={instanceFetch.value.enterprise}
                roles={rolesFetch.value}
                onClickAddData={handleAddData}
                onClickReview={handleReview}
                onClickChoosePackage={handleChoosePackage}
                onClickAddPaymentMethod={handleAddPaymentMethod}
                onClickAcceptCalibrationJobs={
                    hasAppPermission(
                        getValue(rolesFetch) ?? [],
                        "settings:update"
                    )
                        ? showConfirm
                        : undefined
                }
                onClickLabelMyself={handleLabelMyself}
                onClickReviewTaskDesign={handleReviewTaskDesign}
            />
            {isConfirmShown ? (
                <Confirm
                    title="Confirm initial pricing"
                    description={
                        <>
                            <b>
                                Each processed data point costs $
                                {price.toFixed(2)}
                            </b>{" "}
                            to start with.
                            <br />
                            You can choose from a range of customized pricing
                            options once we've better calibrated our performance
                            on your data.
                        </>
                    }
                    onClickYes={handleClickAcceptCalibrationJobs}
                    onClose={hideConfirm}
                    onClickNo={hideConfirm}
                    yesLabel="Accept pricing"
                />
            ) : null}
        </>
    )
}
